import { useEffect, useRef, useState } from 'react';

export default function Skills() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const coreSkills = [
    { name: 'User Research', icon: 'ri-search-line' },
    { name: 'Information Architecture', icon: 'ri-organization-chart' },
    { name: 'User Flows & Wireframing', icon: 'ri-flow-chart' },
    { name: 'Usability Testing', icon: 'ri-test-tube-line' },
    { name: 'High Fidelity UI Design', icon: 'ri-palette-line' },
    { name: 'Design Systems & Components', icon: 'ri-stack-line' },
    { name: 'Interaction Design / Microinteractions', icon: 'ri-cursor-line' },
    { name: 'Responsive Design', icon: 'ri-layout-grid-line' },
    { name: 'Problem Solving & Critical Thinking', icon: 'ri-lightbulb-line' },
    { name: 'Cross functional Collaboration', icon: 'ri-team-line' },
  ];

  const tools = [
    { name: 'Figma', icon: 'ri-figma-line' },
    { name: 'Framer', icon: 'ri-window-line' },
    { name: 'Invision Studio', icon: 'ri-eye-line' },
    { name: 'Figjam', icon: 'ri-sticky-note-line' },
    { name: 'Google Workspace', icon: 'ri-google-line' },
  ];

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="relative py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Expertise</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Skills & Tools
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            A comprehensive toolkit for creating exceptional user experiences
          </p>
        </div>

        {/* Core Competencies */}
        <div className="mb-24">
          <h3 className="text-3xl font-bold text-slate-900 mb-10 text-center">Core Competencies</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreSkills.map((skill, index) => (
              <div
                key={skill.name}
                className={`bg-slate-50 border-2 border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: '#6b3d52' }}>
                  <i className={`${skill.icon} text-3xl text-white`}></i>
                </div>
                <span className="text-sm font-semibold text-slate-900 leading-tight">{skill.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Design Tools */}
        <div>
          <h3 className="text-3xl font-bold text-slate-900 mb-10 text-center">Design Tools & Technologies</h3>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {tools.map((tool, index) => (
              <div
                key={tool.name}
                className={`bg-slate-50 border-2 border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-500 flex flex-col items-center justify-center text-center ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
              >
                <div className="w-20 h-20 flex items-center justify-center rounded-xl bg-white border-2 border-slate-200 mb-3">
                  <i className={`${tool.icon} text-4xl`} style={{ color: '#6b3d52' }}></i>
                </div>
                <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">{tool.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
